import { useParams, Link } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useAppState } from './AppLayout'
import { useClosestStops } from '@/lib/queries'
import { api, AREAS, greekToLatin } from '@/lib/api'
import type { NearbyStop, RouteForStop, StopInfo } from '@/lib/api'
import { useEffect, useRef, useMemo } from 'react'

const ROUTE_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b',
  '#8b5cf6', '#ec4899', '#06b6d4', '#f97316',
  '#84cc16', '#14b8a6', '#6366f1', '#d946ef',
]

const stopIcon = new L.DivIcon({
  className: 'map-marker stop-marker',
  html: '<div class="marker-inner"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7]
})

function FitBoundsToStops({ stops, center }: { stops: NearbyStop[]; center: [number, number] }) {
  const map = useMap()
  const hasFitted = useRef(false)

  useEffect(() => {
    if (hasFitted.current) return

    if (stops.length === 0) {
      map.setView(center, 15)
      hasFitted.current = true
      return
    }

    const points: [number, number][] = stops.map(s => [
      parseFloat(s.StopLat),
      parseFloat(s.StopLng)
    ])

    const bounds = L.latLngBounds(points)
    map.fitBounds(bounds, { padding: [30, 30] })
    hasFitted.current = true
  }, [stops, center, map])

  return null
}

interface RouteLineData {
  routeCode: string
  lineId: string
  color: string
  stops: StopInfo[]
}

function AreaMap({
  stops,
  routeLines,
  center,
  lang
}: {
  stops: NearbyStop[]
  routeLines: RouteLineData[]
  center: [number, number]
  lang: 'el' | 'en'
}) {
  const getStopName = (s: NearbyStop) =>
    lang === 'en' ? (s.StopDescrEng || s.StopDescr) : s.StopDescr

  return (
    <div className="map-view-container area-map">
      <MapContainer
        center={center}
        zoom={15}
        className="map-view"
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBoundsToStops stops={stops} center={center} />

        {routeLines.map(({ routeCode, stops: routeStops, color }) => {
          const coords: [number, number][] = routeStops.map(s => [
            parseFloat(s.StopLat),
            parseFloat(s.StopLng)
          ])
          if (coords.length < 2) return null

          return (
            <Polyline
              key={routeCode}
              positions={coords}
              pathOptions={{
                color,
                weight: 4,
                opacity: 0.7,
                lineCap: 'round',
                lineJoin: 'round'
              }}
            />
          )
        })}

        {stops.map(s => (
          <Marker
            key={s.StopCode}
            position={[parseFloat(s.StopLat), parseFloat(s.StopLng)]}
            icon={stopIcon}
          >
            <Tooltip direction="top" offset={[0, -8]} className="map-tooltip">
              <div className="tooltip-content">
                <span className="tooltip-name">{getStopName(s)}</span>
              </div>
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  )
}

function AreaSelector({ currentCode, lang }: { currentCode: string | undefined; lang: 'el' | 'en' }) {
  return (
    <div className="area-selector">
      <div className="area-chips">
        {AREAS.map(area => (
          <Link
            key={area.code}
            to={`/area/${area.code}`}
            className={`area-chip ${currentCode === area.code ? 'active' : ''}`}
          >
            {lang === 'en' ? area.nameEng : area.name}
          </Link>
        ))}
      </div>
    </div>
  )
}

export function AreaPage() {
  const { areaCode } = useParams<{ areaCode: string }>()
  const { lang, t } = useAppState()

  const area = AREAS.find(a => a.code === areaCode)
  const { data: stops, isLoading: stopsLoading } = useClosestStops(area?.lat, area?.lng)

  const routesQueries = useQueries({
    queries: (stops || []).slice(0, 15).map((stop: NearbyStop) => ({
      queryKey: ['routesForStop', stop.StopCode],
      queryFn: () => api.getRoutesForStop(stop.StopCode),
      staleTime: 5 * 60 * 1000,
      enabled: !!stop.StopCode,
    })),
  })

  const allRoutes = useMemo(() => {
    const routeMap = new Map<string, RouteForStop>()
    routesQueries.forEach(q => {
      if (q.data) {
        q.data.forEach((r: RouteForStop) => {
          if (!routeMap.has(r.RouteCode)) {
            routeMap.set(r.RouteCode, r)
          }
        })
      }
    })
    return Array.from(routeMap.values())
  }, [routesQueries])

  const routeStopsQueries = useQueries({
    queries: allRoutes.slice(0, 12).map((route: RouteForStop, index: number) => ({
      queryKey: ['stops', route.RouteCode],
      queryFn: () => api.getStops(route.RouteCode),
      staleTime: 5 * 60 * 1000,
      enabled: !!route.RouteCode,
      meta: { color: ROUTE_COLORS[index % ROUTE_COLORS.length], lineId: route.LineID }
    })),
  })

  const routeLines: RouteLineData[] = useMemo(() => {
    return allRoutes.slice(0, 12).map((route, index) => ({
      routeCode: route.RouteCode,
      lineId: route.LineID,
      color: ROUTE_COLORS[index % ROUTE_COLORS.length],
      stops: (routeStopsQueries[index]?.data || []) as StopInfo[]
    })).filter(r => r.stops.length > 0)
  }, [allRoutes, routeStopsQueries])

  const isLoading = stopsLoading || routesQueries.some(q => q.isLoading)
  const isLoadingRoutes = routeStopsQueries.some(q => q.isLoading)

  if (!areaCode) {
    return (
      <div className="area-page">
        <h1>{lang === 'en' ? 'Select an Area' : 'Επιλέξτε Περιοχή'}</h1>
        <AreaSelector currentCode={undefined} lang={lang} />
      </div>
    )
  }

  if (!area) {
    return (
      <div className="area-page">
        <div className="error-message">
          {lang === 'en' ? 'Area not found' : 'Η περιοχή δεν βρέθηκε'}
        </div>
        <AreaSelector currentCode={areaCode} lang={lang} />
      </div>
    )
  }

  const areaName = lang === 'en' ? area.nameEng : area.name

  return (
    <div className="area-page">
      <div className="area-header">
        <h1>{areaName}</h1>
        <p className="area-subtitle">
          {lang === 'en'
            ? `${stops?.length || 0} stops nearby`
            : `${stops?.length || 0} στάσεις κοντά`}
        </p>
      </div>

      <AreaSelector currentCode={areaCode} lang={lang} />

      {isLoading ? (
        <div className="loading">{t.loading}</div>
      ) : (
        <>
          {stops && stops.length > 0 && (
            <AreaMap
              stops={stops}
              routeLines={isLoadingRoutes ? [] : routeLines}
              center={[area.lat, area.lng]}
              lang={lang}
            />
          )}

          <div className="area-routes-section">
            <h2>{lang === 'en' ? 'Bus Lines in Area' : 'Γραμμές στην Περιοχή'}</h2>
            {allRoutes.length > 0 ? (
              <div className="area-routes-list">
                {allRoutes.map((route, index) => {
                  const lineSlug = greekToLatin(route.LineID).toLowerCase()
                  const routeDesc = lang === 'en'
                    ? (route.RouteDescrEng || route.RouteDescr)
                    : route.RouteDescr
                  const color = ROUTE_COLORS[index % ROUTE_COLORS.length]

                  return (
                    <Link
                      key={route.RouteCode}
                      to={`/${lineSlug}`}
                      className="area-route-item"
                    >
                      <div
                        className="route-line-badge"
                        style={{ backgroundColor: index < 12 ? color : undefined }}
                      >
                        {route.LineID}
                      </div>
                      <div className="route-details">
                        <div className="route-description">{routeDesc}</div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            ) : (
              <div className="no-routes">
                {lang === 'en' ? 'No routes found in this area' : 'Δεν βρέθηκαν γραμμές σε αυτή την περιοχή'}
              </div>
            )}
          </div>

          <div className="area-stops-section">
            <h2>{lang === 'en' ? 'Nearby Stops' : 'Κοντινές Στάσεις'}</h2>
            {stops && stops.length > 0 ? (
              <div className="area-stops-list">
                {stops.map((stop: NearbyStop) => {
                  const stopName = lang === 'en'
                    ? (stop.StopDescrEng || stop.StopDescr)
                    : stop.StopDescr

                  return (
                    <Link
                      key={stop.StopCode}
                      to={`/stop/${stop.StopCode}`}
                      className="area-stop-item"
                    >
                      <div className="stop-name">{stopName}</div>
                      <div className="stop-street">{stop.StopStreet}</div>
                    </Link>
                  )
                })}
              </div>
            ) : (
              <div className="no-stops">
                {lang === 'en' ? 'No stops found' : 'Δεν βρέθηκαν στάσεις'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
